import { apiFetch } from './api';
import type { IconName } from './icons';

export interface AutomationListItem {
  id: string;
  name: string;
  isActive: boolean;
  webhookToken: string;
  lastRunAt: string | null;
  lastRunStatus: string | null;
  runCount: number;
  failureCount: number;
  createdAt: string;
  updatedAt: string;
}

// =============================================================================
//  Graph (v2) config shape — n8n-inspired nodes[]+edges[].
// =============================================================================

/** Тип ноди — рядок (визначається Node SDK на бекенді). */
export type NodeType = string;

/** Узагальнена нода графа (типи/параметри визначає SDK; UI керується каталогом). */
export interface AutomationNode {
  id: string;
  type: string;
  position: [number, number];
  parameters?: Record<string, unknown>;
}

export interface AutomationEdge {
  id?: string;
  source: string;
  sourceHandle?: string;
  target: string;
  targetHandle?: string;
}

export interface GraphConfig {
  version: number;
  nodes: AutomationNode[];
  edges: AutomationEdge[];
}

export interface AutomationDetail extends AutomationListItem {
  config: GraphConfig;
  version: number;
  lastWebhookPayload: unknown | null;
  lastWebhookReceivedAt: string | null;
}

export interface WorkflowVersionItem {
  id: string;
  version: number;
  label: string | null;
  createdAt: string;
  createdBy: string | null;
}

export interface AutomationRun {
  id: string;
  status: 'success' | 'failed' | 'skipped' | 'queued' | 'running';
  payload: unknown;
  result: unknown;
  error: string | null;
  durationMs: number | null;
  occurredAt: string;
  /** Automations v2 (async): тип тригера. */
  triggerType?: string;
}

export async function listAutomations(): Promise<AutomationListItem[]> {
  const r = await apiFetch<{ items: AutomationListItem[] }>('/admin/automations');
  return r.items;
}

export async function createAutomation(name: string): Promise<string> {
  const r = await apiFetch<{ id: string }>('/admin/automations', {
    method: 'POST',
    body: { name },
  });
  return r.id;
}

export async function getAutomation(id: string): Promise<AutomationDetail> {
  return apiFetch<AutomationDetail>(`/admin/automations/${id}`);
}

export async function updateAutomation(
  id: string,
  body: Partial<{ name: string; isActive: boolean; config: GraphConfig; version: number }>,
): Promise<{ version?: number }> {
  return apiFetch(`/admin/automations/${id}`, { method: 'PATCH', body });
}

export async function listVersions(id: string): Promise<WorkflowVersionItem[]> {
  const r = await apiFetch<{ items: WorkflowVersionItem[] }>(`/admin/automations/${id}/versions`);
  return r.items;
}

export async function restoreVersion(id: string, versionId: string): Promise<{ version: number; config: GraphConfig }> {
  return apiFetch(`/admin/automations/${id}/restore/${versionId}`, { method: 'POST' });
}

export async function deleteAutomation(id: string): Promise<void> {
  await apiFetch(`/admin/automations/${id}`, { method: 'DELETE' });
}

/**
 * Скинути sample-payload — наступний реальний webhook замінить його.
 * Використовується коли структура зовнішньої системи змінилась.
 */
export async function resetSample(id: string): Promise<void> {
  await apiFetch(`/admin/automations/${id}/sample`, { method: 'DELETE' });
}

/** Async-виконання (Automations v2). Замінює listRuns; форма items сумісна. */
export async function listRuns(id: string, limit = 50): Promise<AutomationRun[]> {
  const r = await apiFetch<{ items: AutomationRun[] }>(
    `/admin/automations/${id}/executions?limit=${limit}`,
  );
  return r.items;
}

// =============================================================================
//  Ручний запуск / Тест + дебагер (Phase 4)
// =============================================================================

/** Повний snapshot одного кроку виконання (для Logs/Output-вкладок). */
export interface ExecutionStepDetail {
  nodeId: string;
  nodeType: string;
  status: 'succeeded' | 'failed';
  ok: boolean;
  input: unknown;
  output: unknown;
  branch?: string;
  logs: { level: string; msg: string; data?: unknown; at: string }[];
  error?: string;
  durationMs: number | null;
  startedAt: string | null;
}

export interface ExecutionDetail {
  id: string;
  status: string;
  triggerType: string;
  payload: unknown;
  error: string | null;
  durationMs: number | null;
  startedAt: string | null;
  finishedAt: string | null;
  occurredAt: string;
  steps: ExecutionStepDetail[];
}

/** Запустити автоматизацію вручну (тест). Працює і для неактивних. */
export async function testAutomation(id: string, payload?: unknown): Promise<{ executionId: string }> {
  const r = await apiFetch<{ executionId: string }>(`/admin/automations/${id}/test`, {
    method: 'POST',
    body: payload !== undefined ? { payload } : {},
  });
  return r;
}

/** Повний snapshot виконання з кроками. */
export async function getExecution(id: string, execId: string): Promise<ExecutionDetail> {
  return apiFetch<ExecutionDetail>(`/admin/automations/${id}/executions/${execId}`);
}

/** Live-подія виконання зі стріму. */
export type ExecutionEvent =
  | { kind: 'node-start'; executionId: string; nodeId: string; nodeType: string; ts: number }
  | {
      kind: 'node-finish';
      executionId: string;
      nodeId: string;
      nodeType: string;
      status: 'succeeded' | 'failed';
      branch?: string;
      durationMs: number;
      error?: string;
      ts: number;
    }
  | { kind: 'execution-status'; executionId: string; status: 'running' | 'succeeded' | 'failed'; error?: string; ts: number };

function sseBase(): string {
  const base = import.meta.env.VITE_API_BASE ?? '/api';
  try {
    return new URL(base).origin;
  } catch {
    return '';
  }
}

/**
 * Підписка на SSE-стрім виконання. Повертає функцію відписки.
 * onEvent викликається для кожної ExecutionEvent.
 */
export function subscribeExecutionSSE(execId: string, onEvent: (ev: ExecutionEvent) => void): () => void {
  const url = `${sseBase()}/api/admin/automations/executions/${execId}/stream`;
  let es: EventSource | null = null;
  let closed = false;

  es = new EventSource(url, { withCredentials: true });
  es.addEventListener('execution', (e) => {
    try {
      onEvent(JSON.parse((e as MessageEvent).data) as ExecutionEvent);
    } catch {
      /* ignore malformed */
    }
  });
  es.onerror = () => {
    if (closed) return;
    // Стрім розривається коли виконання завершилось — не ретраїмо агресивно.
  };

  return () => {
    closed = true;
    es?.close();
  };
}


// =============================================================================
//  Креденшали (Phase 5b). Секрети ніколи не повертаються — лише метадані.
// =============================================================================

export type CredentialKind = 'bearer' | 'basic' | 'header' | 'apiKeyQuery' | 'generic';

export interface CredentialItem {
  id: string;
  name: string;
  kind: CredentialKind;
  createdAt: string;
  updatedAt: string;
}

export async function listCredentials(): Promise<CredentialItem[]> {
  const r = await apiFetch<{ items: CredentialItem[] }>('/admin/automation-credentials');
  return r.items;
}

export async function createCredential(
  name: string,
  kind: CredentialKind,
  data: Record<string, unknown>,
): Promise<string> {
  const r = await apiFetch<{ id: string }>('/admin/automation-credentials', {
    method: 'POST',
    body: { name, kind, data },
  });
  return r.id;
}

export async function updateCredential(
  id: string,
  body: Partial<{ name: string; kind: CredentialKind; data: Record<string, unknown> }>,
): Promise<void> {
  await apiFetch(`/admin/automation-credentials/${id}`, { method: 'PATCH', body });
}

export async function deleteCredential(id: string): Promise<void> {
  await apiFetch(`/admin/automation-credentials/${id}`, { method: 'DELETE' });
}

// =============================================================================
//  Каталог node-типів — описи для палітри/properties-панелі.
// =============================================================================

export interface NodeFieldDef {
  key: string;
  label: string;
  hint?: string;
  type?:
    | 'text' | 'expression' | 'select' | 'number' | 'boolean' | 'json' | 'credential'
    | 'funnel' | 'funnel_stage' | 'person' | 'data_table' | 'record_fields' | 'funnel_custom_event';
  options?: { value: string; label: string }[];
  required?: boolean;
  group?: string;
  showWhen?: { field: string; equals: string | number | boolean };
  dependsOn?: string;
  customTablesOnly?: boolean;
}

export type NodeKind = 'trigger' | 'action' | 'logic';
export type NodeCategory = 'Trigger' | 'CRM' | 'Logic' | 'Data' | 'Network' | 'Flow' | 'AI';

export interface NodeTypeDef {
  type: NodeType;
  kind?: NodeKind;
  category?: NodeCategory;
  label: string;
  icon: IconName; // Lucide icon name
  color: string; // tailwind base, e.g. "purple"
  description: string;
  outputs: { id: string; label?: string }[]; // sourceHandle ids
  inputs: { id: string; label?: string }[]; // targetHandle ids
  fields: NodeFieldDef[];
}

/** Каталог node-типів з бекенду (генерується з Node SDK). */
export async function fetchNodeCatalog(): Promise<NodeTypeDef[]> {
  const r = await apiFetch<{ items: NodeTypeDef[] }>('/admin/automations/node-catalog');
  return r.items;
}

export const NODE_CATALOG: Record<NodeType, NodeTypeDef> = {
  'trigger.webhook': {
    type: 'trigger.webhook',
    label: 'Webhook',
    icon: 'automations',
    color: 'blue',
    description: 'Точка входу — зовнішній POST з JSON-тілом.',
    inputs: [],
    outputs: [{ id: 'main' }],
    fields: [],
  },
  'action.upsertPerson': {
    type: 'action.upsertPerson',
    label: 'Створити/оновити Person',
    icon: 'users',
    color: 'purple',
    description: 'resolvePerson + enrich фіналізованими даними.',
    inputs: [{ id: 'main' }],
    outputs: [{ id: 'main' }],
    fields: [
      { key: 'telegramId', label: 'Telegram ID', hint: '$.telegram_id' },
      { key: 'email', label: 'Email', hint: '$.email' },
      { key: 'phone', label: 'Phone', hint: '$.phone' },
      { key: 'sendpulseContactId', label: 'SP Contact ID', hint: '$.sp_contact_id' },
      { key: 'displayName', label: 'Display Name', hint: '$.name' },
      { key: 'username', label: 'Username (@)', hint: '$.username' },
      { key: 'currentTariff', label: 'Поточний тариф', hint: '$.tarif' },
      { key: 'subscriptionEndsAt', label: 'Підписка діє до', hint: '$.sub_ends_at' },
    ],
  },
  'action.createPayment': {
    type: 'action.createPayment',
    label: 'Створити Payment',
    icon: 'payments',
    color: 'emerald',
    description: 'Створити Payment у БД. Привʼязується до Person з контексту (попередня нода).',
    inputs: [{ id: 'main' }],
    outputs: [{ id: 'main' }],
    fields: [
      { key: 'amountUah', label: 'Сума (UAH)', hint: '$.amount', required: true },
      { key: 'status', label: 'Статус', hint: '$.status / "Approved"', required: true },
      { key: 'occurredAt', label: 'Дата платежу', hint: '$.date', required: true },
      { key: 'currency', label: 'Валюта', hint: 'UAH (default)' },
      { key: 'externalRef', label: 'External ref', hint: '$.order_id' },
      { key: 'provider', label: 'Провайдер', hint: 'WAYFORPAY / LIQPAY / ...' },
      { key: 'paymentMethod', label: 'Спосіб оплати', hint: '$.payment_method' },
      { key: 'rawTariff', label: 'Тариф (raw)', hint: '$.tarif' },
      { key: 'payerEmail', label: 'Email платника', hint: '$.email' },
      { key: 'payerPhone', label: 'Телефон платника', hint: '$.phone' },
      { key: 'payerFirstName', label: 'Імʼя', hint: '$.first_name' },
      { key: 'payerLastName', label: 'Прізвище', hint: '$.last_name' },
      { key: 'telegramId', label: 'Telegram ID (resolve)', hint: '$.telegram_id' },
      { key: 'sendpulseContactId', label: 'SP Contact ID (resolve)', hint: '$.sp_contact_id' },
    ],
  },
  'condition.if': {
    type: 'condition.if',
    label: 'Якщо…',
    icon: 'branch',
    color: 'amber',
    description: 'Розгалуження then/else за умовою.',
    inputs: [{ id: 'main' }],
    outputs: [
      { id: 'then', label: 'TRUE' },
      { id: 'else', label: 'FALSE' },
    ],
    fields: [
      { key: 'left', label: 'Зліва', hint: '$.amount', required: true },
      {
        key: 'operator',
        label: 'Оператор',
        type: 'select',
        options: [
          { value: 'eq', label: '= дорівнює' },
          { value: 'neq', label: '≠ не дорівнює' },
          { value: 'gt', label: '> більше' },
          { value: 'gte', label: '≥ більше або дор.' },
          { value: 'lt', label: '< менше' },
          { value: 'lte', label: '≤ менше або дор.' },
          { value: 'contains', label: 'містить' },
          { value: 'startsWith', label: 'починається з' },
          { value: 'exists', label: 'існує' },
          { value: 'notExists', label: 'не існує' },
        ],
        required: true,
      },
      { key: 'right', label: 'Справа', hint: 'число або $.path' },
    ],
  },
};
